/* Kleine Bausteine der Seitenleiste: Zahlenfeld, Textfeld mit Übernehmen beim Verlassen/Enter, Textur-Vorschau. */
import { memo, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { Pixels } from '../../formats/wad.ts';

const fmt = (v: number): string => String(Math.round(v * 1000) / 1000);

/** Zahlenfeld: übernimmt gültige Eingaben sofort, zeigt bei neuem Wert von außen diesen an */
export function NumInput(props: { value: number; onChange: (v: number) => void; width?: string; 'aria-label'?: string }) {
  const [text, setText] = useState(fmt(props.value));
  const last = useRef(props.value);
  if (last.current !== props.value) { last.current = props.value; if (parseFloat(text) !== props.value) setText(fmt(props.value)); }
  return (
    <input type="number" className="inp" step="any" style={props.width ? { width: props.width } : undefined} value={text} aria-label={props['aria-label']}
      onChange={(e) => { setText(e.target.value); const v = parseFloat(e.target.value); if (!isNaN(v)) { last.current = v; props.onChange(v); } }} />
  );
}

/** Textfeld, das erst beim Verlassen oder mit Enter übernimmt (wie ein change-Ereignis) */
export function CommitInput(props: { value: string; onCommit: (v: string) => void; list?: string; placeholder?: string; width?: string; 'aria-label'?: string }) {
  const [text, setText] = useState(props.value);
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setText(props.value); }, [props.value, focus]);
  const commit = () => { if (text !== props.value) props.onCommit(text); };
  return (
    <input type="text" className="inp" spellCheck={false} autoComplete="off" list={props.list} placeholder={props.placeholder} aria-label={props['aria-label']}
      style={props.width ? { width: props.width } : undefined} value={text}
      onFocus={() => setFocus(true)} onChange={(e) => setText(e.target.value)}
      onBlur={() => { setFocus(false); commit(); }} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
  );
}

/** Zeichnet Pixel in ein Canvas; false, wenn es keine gibt oder das Canvas keinen Kontext liefert */
export function drawPixels(canvas: HTMLCanvasElement, px: Pixels | null): boolean {
  if (!px) return false;
  try {
    const ctx = canvas.getContext('2d');
    if (!ctx) return false;
    canvas.width = px.w; canvas.height = px.h;
    const id = ctx.createImageData(px.w, px.h);
    id.data.set(px.rgba); ctx.putImageData(id, 0, 0);
    return true;
  } catch (err) { return false; }
}

// Vorschaubilder werden in Portionen gezeichnet, damit die Seite beim Aufbau von hunderten Bildern bedienbar bleibt
const queue: (() => void)[] = [];
let pumping = false;
function pump(): void {
  for (let n = 0; n < 24 && queue.length; n++) (queue.shift() as () => void)();
  if (queue.length) setTimeout(pump, 0); else pumping = false;
}
function drawLater(fn: () => void): void { queue.push(fn); if (!pumping) { pumping = true; setTimeout(pump, 0); } }

/** Textur-Vorschau; zeichnet neu, wenn sich der Name oder der Stand der WADs (key) ändert. later: in Portionen */
export const TexCanvas = memo(function TexCanvas(props: { name: string; wads: { pixels(n: string): Pixels | null }; version: string; className?: string; style?: React.CSSProperties; later?: boolean; onDrawn?: (ok: boolean) => void }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const { name, wads, version, later, onDrawn } = props;
  useEffect(() => {
    let alive = true;
    const run = () => { if (alive && ref.current) { const ok = drawPixels(ref.current, wads.pixels(name)); onDrawn?.(ok); } };
    if (later) drawLater(run); else run();
    return () => { alive = false; };
  }, [name, version, later, wads, onDrawn]);
  return <canvas ref={ref} width={later ? 8 : 64} height={later ? 8 : 64} className={props.className} style={props.style} />;
});

export const Section = (props: { title: string; children?: ReactNode }) => <><h3>{props.title}</h3>{props.children}</>;

/** Zahlenfeld, das erst beim Verlassen oder mit Enter übernimmt (wie ein change-Ereignis); zeigt neue Werte von außen an, solange es nicht bearbeitet wird */
export function NumCommit(props: { value: number; onCommit: (v: number) => void; digits?: number; width?: string; style?: React.CSSProperties; 'aria-label'?: string }) {
  const f = Math.pow(10, props.digits ?? 3);
  const show = (v: number): string => String(Math.round(v * f) / f);
  const [text, setText] = useState(show(props.value));
  const [focus, setFocus] = useState(false);
  useEffect(() => { if (!focus) setText(show(props.value)); }, [props.value, focus]);   // eslint-disable-line react-hooks/exhaustive-deps
  const commit = () => { const v = parseFloat(text); if (!isNaN(v) && v !== props.value) props.onCommit(v); else setText(show(props.value)); };
  return (
    <input type="number" className="inp" step="any" aria-label={props['aria-label']} style={props.style || (props.width ? { width: props.width } : undefined)} value={text}
      onFocus={() => setFocus(true)} onChange={(e) => setText(e.target.value)}
      onBlur={() => { setFocus(false); commit(); }} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
  );
}
